/**
 * Author: GPT-6 / Codex (reasoning effort not exposed); GPT-6.1 Sol / Codex; GPT-6 Sol / Codex; Claude Opus 5.5
 * Date: 02-November-2026; 02-Nov-2026 (Claude Opus 5.5 High: compact, small-label story cards)
 * PURPOSE: Reusable person pieces for competition reporting: faces (Hall of Fame crop or saved
 *          Kaggle picture, initials when a person has neither), roster chips, the "in this story"
 *          subject cards for every reported person, directory cards and honors that link to the person's Hall of Fame cards.
 *          Used by the people pages, team rosters, editions, dispatches and the community desk.
 * SRP/DRY check: Pass — exact verified account matches reuse the shared identity ledger; every
 *          view draws a person through PersonPortrait so a face looks the same everywhere.
 */
import { useState } from 'react';
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { collectionRoleLabel, collectionShareVersion, type NewsCollection } from '@shared/newsCollection';
import { displayPortrait, personPath, peopleForTeam, newsDate, competitionName, personInitials, shortPersonName, type CompetitorRecord, type NewsCompetition, type NewsPerson } from '@shared/news';

type PortraitSize = 'chip' | 'face' | 'card' | 'hero';

/** A person's face. Decorative when their name is printed right beside it. */
export function PersonPortrait({ person, size, decorative = false }: { person: NewsPerson; size: PortraitSize; decorative?: boolean }) {
  const [failed, setFailed] = useState<string[]>([]);
  const portrait = [displayPortrait(person), person.portrait].find(item => item && !failed.includes(item.src));
  const className = `news-portrait news-portrait-${size}`;
  if (!portrait) return <span className={`${className} news-portrait-initials`} aria-hidden="true">{personInitials(person.name)}</span>;
  return <img className={className} src={portrait.src} alt={decorative ? '' : portrait.alt} loading="lazy" decoding="async" onError={() => setFailed(previous => [...previous, portrait.src])} />;
}

/** Name with face and the person's top honor, for rosters and source lines. */
export function PersonLinks({ person, honors = 1 }: { person: NewsPerson; honors?: number }) {
  return <span className="news-person-chip"><PersonPortrait person={person} size="chip" decorative /><span><Link href={personPath(person.id)}>{person.name} →</Link>{person.hallOfFame.slice(0, honors).map(card => <span key={card.path} className="news-person-history"> · <Link href={card.path}>{card.label} ↗</Link></span>)}</span></span>;
}

/**
 * "In this story": the real ARC Prize card of every person the story names or cites, side by side like a
 * binder page, each linking to its card page. A person with no card yet falls back to the compact portrait
 * tile. Selection is shared with the initial HTML, not inferred from team rosters.
 */
export function StoryPeopleCards({ people, competition }: { people: NewsPerson[]; competition: NewsCompetition }) {
  const cabinet = useQuery<NewsCollection>({
    queryKey: ['/api/news/cards'], staleTime: 60_000,
    queryFn: async () => {
      const response = await fetch('/api/news/cards');
      if (!response.ok) throw new Error('The cards could not be loaded.');
      return response.json() as Promise<NewsCollection>;
    },
  });
  if (!people.length) return null;
  const cardOf = (person: NewsPerson) => cabinet.data?.cards.find(card => card.kind === 'person' && card.personId === person.id);
  const carded = people.map(person => ({ person, card: cardOf(person) }));
  const withCard = carded.filter(item => item.card), without = carded.filter(item => !item.card);
  return <section className="news-story-people" aria-label="In this story">
    <h2 className="news-story-people-label">In this story</h2>
    {!!withCard.length && <div className="news-story-cards">{withCard.map(({ person, card }) => <Link key={card!.id} href={card!.cardUrl} className="news-story-card">
      <img src={`/api/news/cards/${encodeURIComponent(card!.id)}/thumb.webp?side=front&w=300&v=${collectionShareVersion(card!)}`} width={300} height={420} loading="lazy"
        alt={`${person.name}, ${collectionRoleLabel(card!).toLowerCase()} card ${card!.number}`} /><span className="news-story-card-name">{person.name}</span>
    </Link>)}</div>}
    {!!without.length && <div className="news-story-people-grid">{without.map(({ person }) => {
      const team = [...person.memberships].reverse().find(member => member.competition === competition);
      return <Link key={person.id} href={personPath(person.id)} className="news-subject-card">
        <PersonPortrait person={person} size="card" decorative />
        <span className="news-subject-details"><strong>{person.name}</strong>{team && <span>{team.teamName}</span>}{person.hallOfFame[0] && <span className="news-subject-honor">{person.hallOfFame[0].label}</span>}</span>
      </Link>;
    })}</div>}
  </section>;
}

/** What a face is doing in a story: the person's team in this contest, else their top honor. */
function faceCaption(person: NewsPerson, competition?: NewsCompetition) {
  const team = competition && [...person.memberships].reverse().find(member => member.competition === competition);
  return team ? team.teamName : person.hallOfFame[0]?.label;
}

/** The contenders a story is about, labeled as such and captioned with their team so they never read as a byline. */
export function StoryFaces({ people, max = 6, label = 'In this story', competition, showLabel = true }: { people: NewsPerson[]; max?: number; label?: string; competition?: NewsCompetition; showLabel?: boolean }) {
  if (!people.length) return null;
  return <div className="news-faces-block" role="group" aria-label={label}>
    {showLabel && <div className="news-faces-label">{label}</div>}
    <div className="news-faces">{people.slice(0, max).map(person => {
      const caption = faceCaption(person, competition);
      return <Link key={person.id} href={personPath(person.id)} className="news-face"><PersonPortrait person={person} size="face" decorative /><span><strong>{shortPersonName(person.name)}</strong>{caption && <em>{caption}</em>}</span></Link>;
    })}</div>
  </div>;
}

export function TeamPeople({ team, people = [] }: { team: CompetitorRecord; people?: NewsPerson[] }) {
  const known = peopleForTeam(people, team);
  return <div className="news-team-people"><ul>{team.members.map(handle => {
    const person = known.find(item => item.accounts.some(account => account.platform === 'kaggle' && account.handle === handle));
    return <li key={handle}>{person ? <PersonLinks person={person} /> : <a href={`https://www.kaggle.com/${encodeURIComponent(handle)}`}>{handle} ↗</a>}</li>;
  })}</ul><p className="news-muted">Roster observed {newsDate(team.lastObservedAt)}.</p></div>;
}

/** Faces of the verified people on a team, for notebook cards. */
export function TeamFaces({ team, people = [] }: { team: CompetitorRecord; people?: NewsPerson[] }) {
  return <StoryFaces people={peopleForTeam(people, team)} max={4} label={`People on ${team.name}`} competition={team.competition} showLabel={false} />;
}

export function PersonHonors({ person, linked = false }: { person: NewsPerson; linked?: boolean }) {
  if (!person.hallOfFame.length) return null;
  return <ul className="news-honors">{person.hallOfFame.map(card => <li key={card.path}>{linked ? <Link href={card.path}>{card.label}</Link> : card.label}</li>)}</ul>;
}

/** Directory card: face, name, honors, current teams and the first sourced fact. */
export function PersonCard({ person }: { person: NewsPerson }) {
  return <article className="news-person-card">
    <div className="news-person-card-top">
      <Link href={personPath(person.id)} tabIndex={-1} aria-hidden="true"><PersonPortrait person={person} size="card" decorative /></Link>
      <div>
        <h2><Link href={personPath(person.id)}>{person.name}</Link></h2>
        <PersonHonors person={person} />
      </div>
    </div>
    {!!person.memberships.length && <p className="news-card-members">{person.memberships.map(member => `${member.teamName} — ${competitionName(member.competition)} ${member.season}`).join(' · ')}</p>}
    {person.facts[0] && <p>{person.facts[0].text}</p>}
    <Link href={personPath(person.id)} className="news-read">Open the profile →</Link>
  </article>;
}

/** Where a face came from, printed under the profile portrait. */
export function PortraitCredit({ person }: { person: NewsPerson }) {
  if (person.editorialPortrait) return <p className="news-muted">AI editorial portrait based on <a href={person.editorialPortrait.sourceUrl}>{person.editorialPortrait.sourceTitle} ↗</a>.</p>;
  const portrait = person.portrait;
  if (!portrait) return null;
  return <p className="news-muted">{portrait.kind === 'kaggle'
    ? <>Picture: <a href={portrait.sourceUrl}>Kaggle profile ↗</a></>
    : <>Picture: <Link href={portrait.sourceUrl.replace(/^https:\/\/arc\.markbarney\.net/, '')}>ARC Explainer Hall of Fame card →</Link></>}</p>;
}
